import {ApiClient} from '@hconnect/apiclient'
import {useQuery, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {stringify} from 'query-string'

import {
  DEFAULT_CUSTOMIZATIONS_PAGE,
  DEFAULT_CUSTOMIZATIONS_PAGE_LIMIT,
  REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY
} from '../consts'
import type {RefractoryPlacingMethod} from '../enums'
import type {RefractoryPaginated} from '../types'

interface UseRefractoryCustomizationsQueryParams {
  plantUpmId: string
  page?: number
  limit?: number
  ids?: number[]
  name?: string
  manufacturerIds?: number[]
  classificationIds?: number[]
  classificationGroupIds?: number[]
  bondingIds?: number[]
  placingMethods?: RefractoryPlacingMethod[]
  activeStatus?: string
  countryCode?: string[]
  manufacturerDateFrom?: string
  manufacturerDateTo?: string
  enabled?: boolean
}

export const useRefractoryCustomizationsQuery = (
  {
    plantUpmId,
    page,
    limit,
    ids,
    name,
    manufacturerIds,
    classificationIds,
    classificationGroupIds,
    bondingIds,
    placingMethods,
    activeStatus,
    countryCode,
    manufacturerDateFrom,
    manufacturerDateTo,
    enabled = true
  }: UseRefractoryCustomizationsQueryParams,
  options?: Omit<
    UseQueryOptions<RefractoryPaginated, AxiosError, RefractoryPaginated>,
    'queryKey' | 'queryFn' | 'enabled'
  >
): UseQueryResult<RefractoryPaginated, AxiosError> => {
  const resolvedPage = page ?? DEFAULT_CUSTOMIZATIONS_PAGE
  const resolvedLimit = limit ?? DEFAULT_CUSTOMIZATIONS_PAGE_LIMIT

  return useQuery<RefractoryPaginated, AxiosError, RefractoryPaginated>({
    queryKey: [
      REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY,
      plantUpmId,
      resolvedPage,
      resolvedLimit,
      ids,
      name,
      manufacturerIds,
      classificationIds,
      classificationGroupIds,
      bondingIds,
      placingMethods,
      activeStatus,
      countryCode,
      manufacturerDateFrom,
      manufacturerDateTo
    ],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<RefractoryPaginated>(
        `/plants/${plantUpmId}/refractory-product-data-catalog/refractories`,
        {
          params: {
            page: resolvedPage,
            limit: resolvedLimit,
            ids,
            name,
            manufacturerIds,
            classificationIds,
            classificationGroupIds,
            bondingIds,
            placingMethods,
            activeStatus,
            countryCode,
            manufacturerDateFrom,
            manufacturerDateTo
          },
          paramsSerializer: (params: Record<string, unknown>) =>
            stringify(params, {arrayFormat: 'none'}),
          signal
        }
      )
      return response.data
    },
    enabled: enabled && Boolean(plantUpmId),
    ...options
  })
}
